import type { Scene } from '@babylonjs/core';
import { localMetersToLatLng } from '@roadworld/map-generator';
import type { PhysicsWorld } from '@roadworld/physics';
import type {
  ActiveDelivery,
  ClientMsg,
  DeliveryClientMsg,
  DeliveryScore,
  Order,
  PlayerId,
  PlayerIdentity,
  PlayerSnapshot,
  ServerMsg,
  SessionMode,
} from '@roadworld/shared';
import type { IVehicle } from '../vehicle/IVehicle.js';
import { latLngToWorld, type SessionFrame } from '../world/coords.js';
import { multiplayerWsUrl } from './api.js';
import { RemotePlayer } from './RemotePlayer.js';

export interface DeliveryStatePayload {
  mode: SessionMode;
  orders: Order[];
  active: ActiveDelivery[];
  score: DeliveryScore;
  onClock: boolean;
  isHost: boolean;
}

export interface MultiplayerCallbacks {
  onPlayerJoined?(player: PlayerIdentity): void;
  onPlayerLeft?(id: PlayerId): void;
  onWelcome?(me: PlayerIdentity, sessionId: string, sessionEpoch: number): void;
  onTreeFell?(ev: { chunkKey: string; treeIndex: number; hitDirX: number; hitDirZ: number }): void;
  onRejected?(reason: string): void;
  onStatusChange?(status: 'connecting' | 'connected' | 'disconnected'): void;
  // Delivery mode callbacks
  onDeliveryState?(state: DeliveryStatePayload): void;
  onOrderSpawned?(order: Order): void;
  onOrderUpdated?(order: Order): void;
  onOrderRemoved?(orderId: string): void;
  onActiveDeliveries?(active: ActiveDelivery[]): void;
  onScore?(score: DeliveryScore): void;
  onMode?(mode: SessionMode, setBy: PlayerId): void;
  onHostChanged?(hostId: PlayerId): void;
  onOnClock?(on: boolean): void;
  onToast?(text: string, flavor: 'ping' | 'success' | 'fail' | 'info'): void;
}

const SNAPSHOT_INTERVAL_MS = 50; // 20 Hz
const RENDER_DELAY_MS = 100; // one tick of buffering

/**
 * Manages the WebSocket connection, owns the set of RemotePlayer ghosts,
 * throttles own-state snapshots out, and converts incoming snapshot lat/lng
 * to local world coordinates using the current SessionFrame.
 */
export class MultiplayerClient {
  private ws: WebSocket | null = null;
  private scene: Scene;
  private physics: PhysicsWorld;
  private name: string;
  private callbacks: MultiplayerCallbacks;
  private frame: SessionFrame | null = null;

  private myId: PlayerId | null = null;
  private remotes = new Map<PlayerId, RemotePlayer>();
  /** Players the server told us about whose RemotePlayer we haven't built yet (e.g., during early boot). */
  private pendingIdentities = new Map<PlayerId, PlayerIdentity & { lastSnapshot?: PlayerSnapshot }>();

  private lastSnapshotSentAt = 0;
  /** clockOffset = serverNow - localNow at connect time. */
  private clockOffset = 0;
  private sessionId: string | null = null;
  private sessionEpoch: number | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private shouldReconnect = true;

  constructor(
    scene: Scene,
    physics: PhysicsWorld,
    name: string,
    callbacks: MultiplayerCallbacks = {},
  ) {
    this.scene = scene;
    this.physics = physics;
    this.name = name;
    this.callbacks = callbacks;
  }

  /** Provide / update the session frame. Without it, snapshots can't be converted. */
  setFrame(frame: SessionFrame): void {
    this.frame = frame;
    // Re-place any existing remotes against the new frame using their latest snapshot.
    for (const r of this.remotes.values()) {
      const latest = r.buffer.latest();
      if (!latest) continue;
      const { x, z } = latLngToWorld(
        { lat: latest.snap.lat, lng: latest.snap.lng },
        frame,
      );
      r.pushSnapshot(latest.snap, x, latest.snap.alt, z);
    }
  }

  connect(): void {
    this.shouldReconnect = true;
    this.openSocket();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // ignore
      }
      this.ws = null;
    }
    for (const r of this.remotes.values()) r.dispose();
    this.remotes.clear();
  }

  /** Current local-clock time mapped to server clock. */
  serverNow(): number {
    return Date.now() + this.clockOffset;
  }

  /** Session time in ms since session epoch (foundation hook for future deterministic traffic). */
  sessionTime(): number {
    if (this.sessionEpoch === null) return 0;
    return this.serverNow() - this.sessionEpoch;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  myPlayerId(): PlayerId | null {
    return this.myId;
  }

  /** Snapshot iterable for UI (player list, etc.). Includes only remotes, not the local player. */
  *players(): IterableIterator<RemotePlayer> {
    for (const r of this.remotes.values()) yield r;
  }

  /**
   * Send own snapshot if the throttle window has elapsed. Position is converted from local
   * world coords to lat/lng/alt.
   */
  maybeSendSnapshot(now: number, vehicle: IVehicle): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (!this.frame) return;
    if (now - this.lastSnapshotSentAt < SNAPSHOT_INTERVAL_MS) return;
    this.lastSnapshotSentAt = now;

    const pos = vehicle.position();
    const vel = vehicle.velocity();
    const rot = vehicle.rotationQuat();
    const { lat, lng } = localMetersToLatLng(pos.x, pos.z, this.frame.origin.lat, this.frame.origin.lng);
    const msg: ClientMsg = {
      kind: 'snapshot',
      snap: {
        t: this.serverNow(),
        lat,
        lng,
        alt: pos.y,
        qx: rot.x,
        qy: rot.y,
        qz: rot.z,
        qw: rot.w,
        vx: vel.x,
        vy: vel.y,
        vz: vel.z,
        steer: vehicle.steerAngle(),
        speed: vehicle.getForwardSpeed(),
      },
    };
    this.send(msg);
  }

  /** Advance all remote players' interpolation. */
  tickRemotePlayers(dt: number): void {
    const renderTime = this.serverNow() - RENDER_DELAY_MS;
    for (const r of this.remotes.values()) {
      r.tick(renderTime, dt);
    }
  }

  /** Broadcast a tree-fell event. */
  sendTreeFell(chunkKey: string, treeIndex: number, hitDirX: number, hitDirZ: number): void {
    this.send({ kind: 'tree_fell', chunkKey, treeIndex, hitDirX, hitDirZ });
  }

  /** Send a delivery-mode client message. */
  sendDelivery(msg: DeliveryClientMsg): void {
    this.send(msg);
  }

  // ---- internal ----

  private openSocket(): void {
    this.callbacks.onStatusChange?.('connecting');
    const url = multiplayerWsUrl();
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.reconnectAttempts = 0;
      this.send({ kind: 'hello', name: this.name });
    });

    ws.addEventListener('message', (ev) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as ServerMsg;
      } catch {
        return;
      }
      this.handleMessage(msg);
    });

    ws.addEventListener('close', () => {
      this.callbacks.onStatusChange?.('disconnected');
      this.ws = null;
      for (const r of this.remotes.values()) r.dispose();
      this.remotes.clear();
      this.myId = null;
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    });

    ws.addEventListener('error', () => {
      // 'close' fires after 'error' — defer cleanup there.
    });
  }

  private scheduleReconnect(): void {
    const attempt = ++this.reconnectAttempts;
    const delay = Math.min(10000, 500 * 2 ** Math.min(attempt - 1, 4));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  private send(msg: ClientMsg): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(msg));
    } catch {
      // ignore
    }
  }

  private handleMessage(msg: ServerMsg): void {
    switch (msg.kind) {
      case 'welcome': {
        this.myId = msg.you.id;
        this.sessionId = msg.sessionId;
        this.sessionEpoch = msg.sessionEpoch;
        this.clockOffset = msg.serverNow - Date.now();
        this.callbacks.onStatusChange?.('connected');
        this.callbacks.onWelcome?.(msg.you, msg.sessionId, msg.sessionEpoch);
        for (const p of msg.players) {
          this.ensureRemote(p);
          if (p.lastSnapshot) this.applySnapshot(p.lastSnapshot);
        }
        break;
      }
      case 'rejected': {
        this.shouldReconnect = false;
        this.callbacks.onRejected?.(msg.reason);
        break;
      }
      case 'player_joined': {
        this.ensureRemote(msg.player);
        this.callbacks.onPlayerJoined?.(msg.player);
        break;
      }
      case 'player_left': {
        const r = this.remotes.get(msg.id);
        if (r) {
          r.dispose();
          this.remotes.delete(msg.id);
        }
        this.callbacks.onPlayerLeft?.(msg.id);
        break;
      }
      case 'snapshot': {
        this.applySnapshot(msg.snap);
        break;
      }
      case 'tree_fell': {
        this.callbacks.onTreeFell?.({
          chunkKey: msg.chunkKey,
          treeIndex: msg.treeIndex,
          hitDirX: msg.hitDirX,
          hitDirZ: msg.hitDirZ,
        });
        break;
      }
      case 'delivery_state': {
        this.callbacks.onDeliveryState?.({
          mode: msg.mode,
          orders: msg.orders,
          active: msg.you.active,
          score: msg.you.score,
          onClock: msg.you.onClock,
          isHost: msg.you.isHost,
        });
        break;
      }
      case 'delivery_order_spawned': {
        this.callbacks.onOrderSpawned?.(msg.order);
        break;
      }
      case 'delivery_order_updated': {
        this.callbacks.onOrderUpdated?.(msg.order);
        break;
      }
      case 'delivery_order_removed': {
        this.callbacks.onOrderRemoved?.(msg.orderId);
        break;
      }
      case 'delivery_active': {
        this.callbacks.onActiveDeliveries?.(msg.active);
        break;
      }
      case 'delivery_score': {
        this.callbacks.onScore?.(msg.score);
        break;
      }
      case 'delivery_mode_changed': {
        this.callbacks.onMode?.(msg.mode, msg.setBy);
        break;
      }
      case 'delivery_host_changed': {
        this.callbacks.onHostChanged?.(msg.hostId);
        break;
      }
      case 'delivery_on_clock': {
        this.callbacks.onOnClock?.(msg.on);
        break;
      }
      case 'delivery_toast': {
        this.callbacks.onToast?.(msg.text, msg.flavor);
        break;
      }
    }
  }

  private ensureRemote(identity: PlayerIdentity): RemotePlayer {
    let r = this.remotes.get(identity.id);
    if (r) return r;
    r = new RemotePlayer(this.physics, this.scene, identity);
    this.remotes.set(identity.id, r);
    return r;
  }

  private applySnapshot(snap: PlayerSnapshot): void {
    if (snap.id === this.myId) return;
    const r = this.remotes.get(snap.id);
    if (!r) {
      // Haven't been told about this player yet — stash for later.
      this.pendingIdentities.set(snap.id, { id: snap.id, name: '?', color: { r: 0.6, g: 0.6, b: 0.6 }, lastSnapshot: snap });
      return;
    }
    if (!this.frame) {
      // No frame yet — defer by stashing as last snapshot in the pending map.
      // (Once the frame is set, setFrame() will reuse it via buffer.latest()).
      // For now, just push with placeholder world coords; setFrame will re-place.
      r.pushSnapshot(snap, 0, snap.alt, 0);
      return;
    }
    const { x, z } = latLngToWorld({ lat: snap.lat, lng: snap.lng }, this.frame);
    r.pushSnapshot(snap, x, snap.alt, z);
  }
}
